import { HttpMethods, makeRequest } from '../helpers/http.helper';
import { isDemoMode } from '../demo/core';
import {
  PatchUtilitySettingsRequest,
  AddUtilityProofFilesRequest,
  UtilityType,
  UtilityProofPresignedUrlResult,
} from '../types/utility-proof.type';

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'] as const;
export type AllowedExtension = (typeof ALLOWED_EXTENSIONS)[number];

export const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.webp';

export function getAllowedExtension(filename: string): AllowedExtension | null {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext && (ALLOWED_EXTENSIONS as readonly string[]).includes(ext)
    ? (ext as AllowedExtension)
    : null;
}

export const getUtilitySettings = (collectionSiteId: string) =>
  makeRequest({
    url: `/collection_site/${collectionSiteId}/utility-settings`,
    method: HttpMethods.GET,
  });

export const patchUtilitySettings = (
  collectionSiteId: string,
  body: PatchUtilitySettingsRequest
) =>
  makeRequest({
    url: `/collection_site/${collectionSiteId}/utility-settings`,
    method: HttpMethods.PATCH,
    data: body,
  });

export const initOrGetUtilityMonth = (
  collectionSiteId: string,
  year: number,
  month: number
) =>
  makeRequest({
    url: `/collection_site/${collectionSiteId}/utility-month/${year}/${month}`,
    method: HttpMethods.POST,
  });

export const listUtilityProofFiles = (
  collectionSiteId: string,
  year: number,
  month: number,
  utilityType?: UtilityType
) =>
  makeRequest({
    url: `/collection_site/${collectionSiteId}/utility-month/${year}/${month}/proofs`,
    method: HttpMethods.GET,
    params: utilityType ? { utility_type: utilityType } : undefined,
  });

export const addUtilityProofFiles = (
  collectionSiteId: string,
  year: number,
  month: number,
  body: AddUtilityProofFilesRequest
) =>
  makeRequest({
    url: `/collection_site/${collectionSiteId}/utility-month/${year}/${month}/proofs`,
    method: HttpMethods.POST,
    data: body,
  });

/**
 * POST /upload may return either the standard envelope (`result: {...}`) or a flat body
 * (`id`, `upload_url`, `file_path` at the top level).
 */
export function parseUtilityProofPresignedResponse(
  body: unknown
): UtilityProofPresignedUrlResult {
  if (!body || typeof body !== 'object') {
    throw new Error('Invalid upload response: expected JSON object');
  }
  const root = body as Record<string, unknown>;
  const nested =
    root.result &&
    typeof root.result === 'object' &&
    root.result !== null
      ? (root.result as Record<string, unknown>)
      : null;
  const payload = nested ?? root;

  const id = payload.id;
  const upload_url = payload.upload_url;
  const file_path = payload.file_path;

  if (typeof id !== 'string' || !id) {
    throw new Error('Invalid upload response: missing asset id');
  }
  if (typeof upload_url !== 'string' || !upload_url) {
    throw new Error('Invalid upload response: missing upload_url');
  }
  if (typeof file_path !== 'string') {
    throw new Error('Invalid upload response: missing file_path');
  }
  return { id, upload_url, file_path };
}

/** Presigned upload for an evidence-style file; `fileType` is the backend `FileType`. */
export const generatePresignedUploadUrl = (
  originalName: string,
  fileExtension: AllowedExtension,
  fileType: 'utility-proof' | 'hse-evidence',
) =>
  makeRequest({
    url: '/upload',
    method: HttpMethods.POST,
    data: { original_name: originalName },
    params: {
      file_type: fileType,
      file_extension: fileExtension,
    },
  });

export const generateUtilityProofPresignedUrl = (
  originalName: string,
  fileExtension: AllowedExtension
) => generatePresignedUploadUrl(originalName, fileExtension, 'utility-proof');

export const uploadFileToPresignedUrl = async (
  file: File,
  presignedUrl: string
): Promise<void> => {
  // Demo uploads get a `demo://upload/<id>` URL: the file never leaves the browser.
  if (isDemoMode() || presignedUrl.startsWith('demo://')) {
    return;
  }
  const headers: HeadersInit = {};
  if (file.type) {
    headers['Content-Type'] = file.type;
  } else {
    headers['Content-Type'] = 'application/octet-stream';
  }
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers,
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Upload failed: ${response.status} ${response.statusText} - ${errorText}`
    );
  }
};
